<template>
  <ModalLayout v-if="isLoaded" size="xl" class="c-documents-modal">
    <template #header>{{ $t('DOCUMENT_MODAL.TITLE') }}</template>

    <template #body>
      <div class="container">
        <div class="row">
          <div class="col-12 col-sm-6 col-lg-4 col-xxxl-3">
            <div class="c-documents-modal__description">{{ $t('DOCUMENT_MODAL.DESCRIPTION') }}</div>
          </div>
          <div class="col">
            <div class="c-documents-modal__action">
              <RouterButton v-if="editable" class="c-documents-modal__action-btn" variant="primary" @click="saveAll">
                {{ $t('DOCUMENT_MODAL.SAVE_BTN') }}
              </RouterButton>

              <RouterButton
                v-else
                class="c-documents-modal__action-btn btn-outline--dotted"
                variant="outline"
                @click="setEditable"
              >
                {{ $t('DOCUMENT_MODAL.EDIT_BTN') }}
              </RouterButton>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-4 col-lg-4 col-xxxl-3">
            <div class="c-documents-modal__filters">
              <RouterButton
                v-for="item in filters"
                :key="item.slug"
                class="c-documents-modal__filter-btn"
                :variant="selectedGenre === item.slug ? 'secondary' : 'outline'"
                :disabled="genre !== '' && item.slug !== genre"
                @click="filter(item.slug)"
              >
                {{ item.name }}
              </RouterButton>
            </div>
          </div>
          <div class="col">
            <div class="c-documents-modal__exercise-list">
              <template v-if="exerciseItems.length">
                <template v-if="editable">
                  <ExerciseEdit
                    v-for="exerciseItem in exerciseItems"
                    :key="exerciseItem._id"
                    v-model="exerciseItem.name"
                    :removal="exerciseItem.removal"
                    :current="exerciseItem.current"
                    @remove="toggleExerciseRemoval(exerciseItem)"
                    @change="markExerciseUpdated(exerciseItem)"
                  />
                </template>

                <template v-else>
                  <ExercisePreview
                    v-for="exerciseItem in exerciseItems"
                    :key="exerciseItem._id"
                    :name="exerciseItem.name || generateName(exerciseItem.genreName, 0, '')"
                    :genre-name="exerciseItem.genreName"
                    :current="exerciseItem.current"
                    :attachment-count="+exerciseItem.attachmentCount"
                    :attachment-size="+exerciseItem.attachmentSize"
                    @select="selectExercise(exerciseItem)"
                  />
                </template>
              </template>
              <div v-else class="c-documents-modal__empty">
                {{ $t('DOCUMENT_MODAL.EMPTY') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </ModalLayout>
</template>

<script lang="ts" setup>
import { onBeforeMount, ref, watch } from 'vue';
import ModalLayout from '@/components/layout/modal/ModalLayout.vue';
import RouterButton from '@/components/RouterButton.vue';
import ExercisePreview from '@/components/documents/ExercisePreview.vue';
import ExerciseEdit from '@/components/documents/ExerciseEdit.vue';
import useExercises from '@/services/useExercises';
import useGenre from '@/services/useGenre';
import { useUserAssets } from '@/core';
import { useI18n } from 'vue-i18n';
import { VIEWS } from '@/router';
import { Exercise } from '@/model/app/Exercise';
import useModal from '@/services/useModal';
import { sortByProperty } from '@/utils/sort';
import { taskIdDefault } from '@/types/enums/WritingStory.enum';
import { RouteLocationNamedRaw, useRoute, useRouter } from 'vue-router';

interface ExerciseItem {
  _id: string;
  name: string;
  genreName: string;
  attachmentCount: string;
  attachmentSize: string;
  removal?: boolean;
  updated?: boolean;
  current?: boolean;
}

interface DocumentsModalProps {
  genre?: string;
}

const props = withDefaults(defineProps<DocumentsModalProps>(), {
  genre: '',
});

const { findBy, removeManyById, save, exercise, generateName } = useExercises();
const { getAll } = useUserAssets();
const { genres } = useGenre();
const { close } = useModal();
const { params } = useRoute();
const router = useRouter();
const exerciseItems = ref<Array<ExerciseItem>>([]);
const selectedGenre = ref<string>('');
const isLoaded = ref<boolean>(false);
const { t } = useI18n();
const filters = [
  {
    name: t('DOCUMENT_MODAL.FILTER_ALL'),
    slug: '',
  },
  ...genres.value,
];
const editable = ref<boolean>(false);

const setEditable = () => {
  editable.value = true;
};

const toggleExerciseRemoval = (exerciseItem: ExerciseItem) => {
  exerciseItem.removal = !exerciseItem.removal;
};

const markExerciseUpdated = (exerciseItem: ExerciseItem) => {
  exerciseItem.updated = true;
};

const redirectToGenre = async () => {
  const isDefaultTask = params.taskId === taskIdDefault;

  await router.push({
    name: VIEWS.genre,
    path: isDefaultTask ? exerciseLink(params.slug as string, params.taskId as string) : (params.slug as string),
  } as RouteLocationNamedRaw);
};

const saveAll = async () => {
  editable.value = false;

  await removeExercises();
  await updateExercises();
  await refreshExercises();
};

const removeExercises = async () => {
  const itemsForRemoval = exerciseItems.value.filter((item) => item.removal);
  const removingCurrent = itemsForRemoval.some((item) => item.current);

  if (removingCurrent) {
    await redirectToGenre();
  }

  await removeManyById(itemsForRemoval.map((item) => item._id));
  exerciseItems.value = exerciseItems.value.filter((item) => !item.removal);
};

const updateExercises = async () => {
  const genreExercises = await findBy(selectedGenre.value);

  await Promise.all(
    genreExercises.map((genreExercise) => {
      const exerciseForUpdate = exerciseItems.value.find((item) => item.updated && item._id === genreExercise._id);

      if (exerciseForUpdate) {
        genreExercise.name = exerciseForUpdate.name;

        return save(genreExercise, exerciseForUpdate._id === exercise.value?._id);
      }
    }),
  );
};

const refreshExercises = async () => {
  await filter(selectedGenre.value);
};

const filter = async (genre: string) => {
  const items = await findBy(genre);
  const allAssets = items.some((item) => item.attachments?.length) ? await getAll() : [];

  exerciseItems.value = items
    .filter((item) => genres.value.some((genre) => genre.slug === item.genre))
    .map((item) => {
      return {
        _id: item._id,
        name: item.name,
        genreName: genres.value.find((genre) => genre.slug === item.genre).name,
        attachmentSize: allAssets
          .filter((asset) => item.attachments.includes(asset.uuid))
          .reduce((prev, next) => prev + next.size, 0),
        attachmentCount: item.attachments.length,
        createdAt: item.createdAt,
      };
    });

  exerciseItems.value.sort(sortByProperty('createdAt'));

  selectedGenre.value = genre;
  isLoaded.value = true;
};

const exerciseLink = (slug: string, taskId?: string, version?: number) => `/${slug}/write/${taskId}/${version}`;

const selectExercise = async (exerciseItem) => {
  const exerciseData: Exercise = (await findBy(selectedGenre.value)).find(
    (genreExercise) => genreExercise._id === exerciseItem._id,
  );

  router.push({
    path: exerciseLink(
      exerciseData.genre,
      !+exerciseData.task ? taskIdDefault : exerciseData.task,
      exerciseData.version,
    ),
  });
  close();
};

onBeforeMount(async () => {
  await filter(props.genre);
  if (params.version) {
    setCurrentExercise(exercise.value);
  }
});

const setCurrentExercise = (exerciseItem: Exercise) => {
  if (!params.version) return;
  exerciseItems.value.forEach((item) => {
    const currentExercise = item._id === exerciseItem._id;
    item.current = currentExercise;
  });
};

watch([() => params.version, () => exerciseItems.value], () => {
  setCurrentExercise(exercise.value);
});
</script>
